// GuestInboxView — "New from Jonathan" guest inbox (C2, bead jonathan-dvhr).
//
// Faithful port of the inbox column in Jonathan.dc.html ("New from Jonathan":
// initials avatar with a tone gradient, name, NEW badge, time-ago, italic
// Playfair goal quote, service pill, "ready for {stylist}"). Wired to the
// ALREADY-LIVE enriched GET /v1/owner/dashboard conversations rows
// (jonathan/http/owner/dashboard_api.py::_get_recent_conversations):
//   { id, guest_name, summary (=goal), service, preferred_stylist,
//     outcome, is_new, time_ago, updated_at }
//
// Clicking a guest calls onOpenGuest(id) — the C3 dossier drawer opens on that.
// The handler is wired here even though C3 lands separately. No numeric
// confidence anywhere; no quick-reply chips; clean single-path component.

// Tone gradients are not provided by the backend, so derive a stable one per
// guest from their name. Mirrors the palette of gradients in the .dc.html
// (periwinkle, terracotta, olive, taupe) so every guest reads warm + distinct.
import { ASSISTANT_NAME } from '../brand.js';
const TONE_GRADIENTS = [
  'linear-gradient(140deg,#8D9FF1,#5566c9)', // periwinkle
  'linear-gradient(140deg,#C5512F,#8f3a20)', // terracotta
  'linear-gradient(140deg,#56683F,#33401E)', // olive
  'linear-gradient(140deg,#9c8468,#6f5c46)', // taupe
  'linear-gradient(140deg,#C79A3E,#94701f)', // ochre
];

function toneFor(name) {
  const key = name || '';
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  return TONE_GRADIENTS[Math.abs(hash) % TONE_GRADIENTS.length];
}

function initialsFor(name) {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function GuestCard({ guest, onOpenGuest }) {
  const name = guest.guest_name || 'New guest';
  const goal = guest.summary || '';
  const service = guest.service;
  const stylist = guest.preferred_stylist;

  return (
    <div
      class="card card-interactive"
      onClick={() => onOpenGuest?.(guest.id)}
      style={{ padding: '18px 20px' }}
    >
      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
        <div
          style={{
            flex: 'none',
            width: '50px',
            height: '50px',
            borderRadius: '14px',
            background: toneFor(name),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#FBF8F1',
            fontWeight: 700,
            fontSize: '16px',
            letterSpacing: '.02em',
          }}
        >
          {initialsFor(name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
              marginBottom: '5px',
            }}
          >
            <span style={{ fontSize: '16px', fontWeight: 700, color: 'var(--ink)' }}>
              {name}
            </span>
            {guest.is_new && (
              <span
                style={{
                  fontSize: '10.5px',
                  fontWeight: 800,
                  letterSpacing: '.08em',
                  color: 'var(--olive)',
                  background: 'var(--olive-chip)',
                  padding: '3px 8px',
                  borderRadius: '100px',
                }}
              >
                NEW
              </span>
            )}
            <span
              style={{
                marginLeft: 'auto',
                fontSize: '12.5px',
                fontWeight: 600,
                color: 'var(--ink-faint)',
              }}
            >
              {guest.time_ago || ''}
            </span>
          </div>
          {goal && (
            <div
              class="font-serif"
              style={{
                fontStyle: 'italic',
                fontSize: '17px',
                lineHeight: 1.35,
                color: '#3A352C',
                marginBottom: '12px',
              }}
            >
              {'“'}
              {goal}
              {'”'}
            </div>
          )}
          {(service || stylist) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {service && (
                <span
                  style={{
                    fontSize: '12.5px',
                    fontWeight: 700,
                    color: '#3C4730',
                    background: '#E9ECDF',
                    padding: '5px 11px',
                    borderRadius: '100px',
                  }}
                >
                  {service}
                </span>
              )}
              {stylist && (
                <span
                  style={{
                    fontSize: '12.5px',
                    fontWeight: 600,
                    color: 'var(--ink-soft)',
                  }}
                >
                  {service ? '·  ' : ''}ready for {stylist}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function GuestInboxView({ dashboardData, onOpenGuest }) {
  // The enriched inbox feed is served on the dashboard payload itself
  // (already-live endpoint), so no separate fetch is needed here. The 3
  // conversation/today endpoints (C3/C4) are not deployed yet, but THIS feed
  // is — and we degrade gracefully if it is missing or empty.
  const conversations = Array.isArray(dashboardData?.conversations)
    ? dashboardData.conversations
    : [];

  return (
    <div
      class="view active fixed inset-0 overflow-y-auto"
      style={{
        background: 'var(--bg)',
        paddingBottom: 'calc(80px + var(--safe-area-bottom))',
      }}
    >
      <main
        class="desktop-container"
        style={{ padding: 'calc(40px + var(--safe-area-top)) 32px 80px' }}
      >
        {/* section header — matches the .dc.html "New from Jonathan" column */}
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: '16px',
            marginBottom: '16px',
            flexWrap: 'wrap',
          }}
        >
          <h2
            class="font-serif"
            style={{ fontWeight: 600, fontSize: '23px', color: 'var(--ink)', margin: 0 }}
          >
            New from {ASSISTANT_NAME}
          </h2>
          <span
            style={{ fontSize: '13px', fontWeight: 600, color: 'var(--ink-soft)' }}
          >
            Tap any guest to see their dossier
          </span>
        </div>

        {conversations.length === 0 ? (
          <div
            class="card"
            style={{
              textAlign: 'center',
              padding: '40px 24px',
              color: 'var(--ink-soft)',
            }}
          >
            <div
              class="font-serif"
              style={{
                fontStyle: 'italic',
                fontSize: '18px',
                color: '#3A352C',
                marginBottom: '6px',
              }}
            >
              No new guests yet.
            </div>
            <div style={{ fontSize: '13.5px', fontWeight: 600 }}>
              When someone reaches out through your widget, their dossier lands here.
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {conversations.map((guest) => (
              <GuestCard key={guest.id} guest={guest} onOpenGuest={onOpenGuest} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
